import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDisclosure } from '@chakra-ui/react';
import {
  getFulfillmentTimelinesV2,
  createOrUpdateFulfillmentETAConfigV2,
  updateFulfillmentTimelineV2TimeUnit
} from '@store/actions/settlementTimelineManagerActions';
import { processRangeWithOverlap } from './rangeUtils';
import { TIME_UNITS } from '@constants/v2/common';

/**
 * Generic function to set nested values in an object using dot notation path.
 * Supports array indices like 'receiveAmountRange[0]' for updating nested array elements.
 * Used internally by useFormDataUpdate to handle complex nested state updates.
 */
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const target = keys.reduce((acc, key) => {
    // Handle array indices like 'receiveAmountRange[0]'
    const match = key.match(/(.+)\[(\d+)\]/);
    if (match) {
      return acc[match[1]][parseInt(match[2])];
    }
    return acc[key];
  }, obj);
  target[lastKey] = value;
};

/**
 * useTimelineData Hook
 *
 * Manages the fulfillment timeline data fetching, state, and country selection.
 * Fetches timeline configuration from Redux, stores data in minutes (no conversion),
 * and provides the current time unit from Redux for display purposes.
 */
export const useTimelineData = (selectedVersion) => {
  const dispatch = useDispatch();
  const { data: fulfillmentTimelineData, isLoading, isError, error, timeUnit } = useSelector(
    (state) => state.settlementTimelineManager.fulfillmentTimelinesV2
  );

  const [formData, setFormData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Memoized countries list
  const countries = useMemo(() => {
    return formData?.config?.countries ? Object.keys(formData.config.countries).sort() : [];
  }, [formData?.config?.countries]);

  const fetchData = useCallback(() => {
    dispatch(
      getFulfillmentTimelinesV2({
        version: selectedVersion,
        onSuccess: (data) => {
          // Store data as-is (in minutes) - no conversion
          setFormData(data);
          if (data?.config?.countries) {
            const countryList = Object.keys(data.config.countries);
            if (countryList.length > 0) {
              setSelectedCountry(countryList[0]);
            }
          }
          setHasUnsavedChanges(false);
        }
      })
    );
  }, [dispatch, selectedVersion]);

  useEffect(() => {
    if (selectedVersion) {
      fetchData();
    }
  }, [selectedVersion, fetchData]);

  useEffect(() => {
    if (fulfillmentTimelineData && !formData) {
      // Store data as-is (in minutes) - no conversion
      setFormData(fulfillmentTimelineData);
      if (fulfillmentTimelineData?.config?.countries) {
        const countryList = Object.keys(fulfillmentTimelineData.config.countries);
        if (countryList.length > 0 && !selectedCountry) {
          setSelectedCountry(countryList[0]);
        }
      }
    }
  }, [fulfillmentTimelineData, formData, selectedCountry]);

  return {
    formData,
    setFormData,
    selectedCountry,
    setSelectedCountry,
    countries,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isLoading,
    isError,
    error,
    fetchData,
    timeUnit
  };
};

/**
 * useFormDataUpdate Hook
 *
 * Provides handlers for updating form data at different nesting levels (top-level, country, beneficiary, range).
 * Uses setNestedValue to handle complex nested object updates and marks the form as having unsaved changes.
 * This centralizes all form update logic to maintain consistency across the application.
 */
export const useFormDataUpdate = (setFormData, setHasUnsavedChanges) => {
  const updateFormData = useCallback((path, value) => {
    setFormData((prev) => {
      const newData = structuredClone(prev);
      setNestedValue(newData, path, value);
      return newData;
    });
    setHasUnsavedChanges(true);
  }, [setFormData, setHasUnsavedChanges]);

  const handleTopLevelChange = useCallback((field, value) => {
    updateFormData(field, value);
  }, [updateFormData]);

  const handleCountryLevelChange = useCallback((country, field, value) => {
    updateFormData(`config.countries.${country}.${field}`, value);
  }, [updateFormData]);

  const handleBeneficiaryLevelChange = useCallback((country, beneficiaryType, field, value) => {
    updateFormData(`config.countries.${country}.beneficiaryTypes.${beneficiaryType}.${field}`, value);
  }, [updateFormData]);

  const handleRangeChange = useCallback((country, beneficiaryType, rangeIndex, field, value) => {
    updateFormData(
      `config.countries.${country}.beneficiaryTypes.${beneficiaryType}.receiveAmountRange[${rangeIndex}].${field}`,
      value
    );
  }, [updateFormData]);

  return {
    handleTopLevelChange,
    handleCountryLevelChange,
    handleBeneficiaryLevelChange,
    handleRangeChange
  };
};

/**
 * useEditMode Hook
 *
 * Manages edit mode state, save/cancel operations, and confirmation modal for updating timeline configurations.
 * Handles validation before saving, prevents data loss by confirming unsaved changes, and dispatches
 * the update action to the backend. Data is sent in minutes format without any conversion.
 */
export const useEditMode = (formData, fetchData, setFormData, setHasUnsavedChanges, hasUnsavedChanges) => {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    isOpen: isConfirmUpdateModalOpen,
    onOpen: onConfirmUpdateModalOpen,
    onClose: onConfirmUpdateModalClose
  } = useDisclosure();

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
    fetchData();
    setIsEditMode(false);
    setHasUnsavedChanges(false);
  };

  const handleSave = () => {
    // Validation
    if (!formData.globalEtaInMinutes && formData.isGlobalEtaEnabled) {
      return;
    }
    onConfirmUpdateModalOpen();
  };

  const handleConfirmSave = useCallback(() => {
    setIsModalLoading(true);

    // Data is already in minutes, no conversion needed
    const payload = {
      version: formData.version,
      isPollingEnabled: formData.isPollingEnabled || false,
      isGlobalEtaEnabled: formData.isGlobalEtaEnabled || false,
      globalEtaInMinutes: formData.globalEtaInMinutes,
      config: formData.config
    };

    dispatch(
      createOrUpdateFulfillmentETAConfigV2({
        payload,
        onSuccess: () => {
          setIsEditMode(false);
          setHasUnsavedChanges(false);
          fetchData();
        },
        onComplete: () => {
          setIsModalLoading(false);
          onConfirmUpdateModalClose();
        }
      })
    );
  }, [formData, dispatch, setIsEditMode, setHasUnsavedChanges, setIsModalLoading, onConfirmUpdateModalClose, fetchData]);

  const handleLoadVersion = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to load a new version?')) {
        return;
      }
    }
    fetchData();
  };

  return {
    isEditMode,
    isModalLoading,
    isConfirmUpdateModalOpen,
    onConfirmUpdateModalClose,
    handleEditMode,
    handleCancelEdit,
    handleSave,
    handleConfirmSave,
    handleLoadVersion
  };
};

/**
 * useAddRange Hook
 *
 * Manages the Add Range modal state and handles adding new amount ranges to beneficiary types.
 * Processes new ranges with overlap handling using processRangeWithOverlap utility to ensure
 * ranges don't conflict and maintains proper sequencing. Supports unbounded (infinite) max ranges.
 */
export const useAddRange = (setFormData, setHasUnsavedChanges) => {
  const [currentAddRangeContext, setCurrentAddRangeContext] = useState(null);
  const {
    isOpen: isAddRangeModalOpen,
    onOpen: onAddRangeModalOpen,
    onClose: onAddRangeModalClose
  } = useDisclosure();

  const handleAddRange = useCallback((country, beneficiaryType) => {
    // Store context for the modal
    setCurrentAddRangeContext({ country, beneficiaryType });
    onAddRangeModalOpen();
  }, [onAddRangeModalOpen]);

  const handleAddRangeSubmit = useCallback((formData) => {
    if (!currentAddRangeContext) return;

    const { country, beneficiaryType } = currentAddRangeContext;

    try {
      setFormData((prev) => {
        const newData = structuredClone(prev);
        const currentRanges = newData.config.countries[country].beneficiaryTypes[beneficiaryType].receiveAmountRange || [];

        // Check if this should be the last range (unbounded)
        const isUnbounded = formData.maxAmount === null || formData.maxAmount === '' || formData.isUnbounded;

        // Create new range from form data
        const newRange = {
          minReceiveAmount: parseFloat(formData.minAmount) || 0,
          maxReceiveAmount: isUnbounded ? null : (parseFloat(formData.maxAmount) || 0),
          etaInMinutes: parseFloat(formData.eta) || 0
        };

        // Process ranges with overlap handling
        const processedRanges = processRangeWithOverlap(currentRanges, newRange);

        // Update the ranges with processed result
        newData.config.countries[country].beneficiaryTypes[beneficiaryType].receiveAmountRange = processedRanges;

        return newData;
      });

      setHasUnsavedChanges(true);
      onAddRangeModalClose();
      setCurrentAddRangeContext(null);
    } catch (error) {
      console.error('Error adding range:', error.message);
      // You could show an error toast here if needed
    }
  }, [currentAddRangeContext, onAddRangeModalClose, setFormData, setHasUnsavedChanges]);

  return {
    isAddRangeModalOpen,
    onAddRangeModalClose,
    handleAddRange,
    handleAddRangeSubmit
  };
};
